import React, { useState } from 'react';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId?: string;
}

export function PrivateRoomInviteModal({ isOpen, onClose, roomId = 'private' }: InviteModalProps) {
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [desktopLink, setDesktopLink] = useState<string | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedDesktop, setCopiedDesktop] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/invites/private', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomId,
          recipientEmail: recipientEmail.trim() || undefined,
          recipientPhone: recipientPhone.trim() || undefined,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate invite token');
      const data = await res.json();
      
      const origin = window.location.origin;
      const fullLink = `${origin}/?invite=${data.token}`;
      setInviteUrl(fullLink);

      const payload = {
        v: 1,
        tenantId: 'default',
        token: data.token,
        signature: data.signature || 'sig_local',
        expiresAt: Date.now() + 86400000,
        roomId,
      };
      const b64 = btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      setDesktopLink(`msng3r://join?payload=${encodeURIComponent(b64)}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error generating invite');
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!inviteUrl) return;
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleCopyDesktop() {
    if (!desktopLink) return;
    navigator.clipboard.writeText(desktopLink);
    setCopiedDesktop(true);
    setTimeout(() => setCopiedDesktop(false), 2000);
  }

  function handleWhatsAppShare() {
    if (!inviteUrl) return;
    const cleanPhone = recipientPhone.replace(/[^0-9]/g, '');
    const msg = encodeURIComponent(`Join my private room #${roomId} on Sovereign Mesh: ${inviteUrl}`);
    const waUrl = cleanPhone ? `https://wa.me/${cleanPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(waUrl, '_blank');
  }

  function handleEmailShare() {
    if (!inviteUrl) return;
    const subject = encodeURIComponent(`Private Room Invitation: #${roomId}`);
    const body = encodeURIComponent(
      `Hello,\n\nYou have been invited to a sovereign private video and chat room on #${roomId}.\n\nJoin directly by clicking your single-use invite link:\n${inviteUrl}\n\nNote: This link is single-use and will prompt the host for admission when you knock.`
    );
    const emailTo = recipientEmail.trim();
    window.open(`mailto:${emailTo}?subject=${subject}&body=${body}`, '_blank');
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-xl shadow-2xl p-6 text-white space-y-4 animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <h3 className="text-base font-semibold">
              Invite Guest to <span className="text-emerald-400 font-mono">#{roomId}</span>
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-lg font-bold p-1"
          >
            ✕
          </button>
        </div>

        <p className="text-xs text-neutral-400 leading-relaxed">
          Generates a <strong>one-time, single-use access link</strong>. When the guest clicks it, they enter your waiting room and knock.
        </p>

        {/* Recipient Tracking Fields (Optional) */}
        <div className="grid grid-cols-2 gap-2 bg-neutral-950 p-2.5 rounded-lg border border-neutral-800">
          <div className="space-y-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">
              Guest Email
            </label>
            <input
              type="email"
              placeholder="guest@domain.com"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs bg-neutral-900 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-600 focus:outline-none focus:border-cyan-400"
            />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-neutral-400">
              WhatsApp / Mobile
            </label>
            <input
              type="tel"
              placeholder="+1 555-0199"
              value={recipientPhone}
              onChange={(e) => setRecipientPhone(e.target.value)}
              className="w-full px-2.5 py-1.5 text-xs bg-neutral-900 border border-neutral-700 rounded text-neutral-200 placeholder-neutral-600 focus:outline-none focus:border-cyan-400"
            />
          </div>
        </div>

        {error && (
          <div className="p-2.5 bg-red-950/60 border border-red-800 rounded text-xs text-red-300">
            {error}
          </div>
        )}

        {!inviteUrl ? (
          <div className="pt-1">
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-neutral-800 text-white text-xs font-semibold rounded-lg shadow transition"
            >
              {loading ? 'Creating One-Time Token...' : 'Generate Guest Invite Link'}
            </button>
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            <div className="space-y-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider text-neutral-400">
                Single-Use Guest URL (Valid for 24h)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={inviteUrl}
                  className="w-full px-3 py-2 text-xs bg-neutral-950 border border-neutral-800 rounded-lg text-neutral-200 select-all font-mono"
                />
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 text-xs font-medium bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg flex-shrink-0 transition"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {desktopLink && (
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono uppercase tracking-wider text-cyan-400">
                  Desktop Client Protocol (msng3r://)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={desktopLink}
                    className="w-full px-3 py-2 text-xs bg-neutral-950 border border-cyan-900/60 rounded-lg text-cyan-200 select-all font-mono truncate"
                  />
                  <button
                    onClick={handleCopyDesktop}
                    className="px-4 py-2 text-xs font-medium bg-cyan-900/40 hover:bg-cyan-800/50 border border-cyan-700/60 text-cyan-200 rounded-lg flex-shrink-0 transition"
                  >
                    {copiedDesktop ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 pt-1">
              <button
                onClick={handleWhatsAppShare}
                className="py-2 px-2 bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/50 text-emerald-300 text-xs font-medium rounded-lg flex items-center justify-center gap-1.5 transition"
                title="Send invite via WhatsApp"
              >
                <span>💬 WhatsApp</span>
              </button>
              <button
                onClick={handleEmailShare}
                className="py-2 px-2 bg-cyan-950/40 hover:bg-cyan-900/40 border border-cyan-800 text-cyan-300 text-xs font-medium rounded-lg flex items-center justify-center gap-1.5 transition"
                title="Send invite via Email"
              >
                <span>✉️ Email</span>
              </button>
              <button
                onClick={handleGenerate}
                className="py-2 px-2 bg-neutral-800 hover:bg-neutral-700 text-neutral-300 text-xs font-medium rounded-lg transition"
              >
                + New Link
              </button>
            </div>
          </div>
        )}

        <div className="pt-2 border-t border-neutral-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-xs text-neutral-400 hover:text-white rounded"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
